import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { PiPencilBold } from 'react-icons/pi';
import Header from '../../../../../../header/header';
import Navvbar from '../../../../../../header/Navvbar';
import Swal from 'sweetalert2'; // For beautiful alerts

const AssignAccounts = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Get shipping type info passed from previous page
  const { shippingType, shippingId, shippingService } = location.state || {};

  // State management
  const [accounts, setAccounts] = useState([]); // All accounts
  const [selectedAccounts, setSelectedAccounts] = useState([]); // Selected account IDs
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all accounts from backend
  const fetchAccounts = async () => {
    try {
      const res = await fetch('https://hms.backend.hiragroupcloud.com/api/account/account');
      const json = await res.json();

      if (json.message === 'Data fetched successfully.' && Array.isArray(json.data)) {
        const accountsData = json.data;
        setAccounts(accountsData);

        // Pre-select accounts that already use this shipping service
        const preSelectedAccounts = accountsData
          .filter(account => {
            if (!account.shippingService) return false;

            if (shippingType) {
              return account.shippingService.service === shippingType;
            }

            if (shippingService) {
              return account.shippingService.service === shippingService.service;
            }

            return account.shippingService._id === shippingId;
          })
          .map(account => account._id);

        setSelectedAccounts(preSelectedAccounts);

        // Auto-check "Select All" if all accounts are pre-selected
        if (preSelectedAccounts.length === accountsData.length && accountsData.length > 0) {
          setSelectAll(true);
        }
      } else {
        setError('Failed to load accounts');
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Load accounts when component mounts or shipping info changes
  useEffect(() => {
    fetchAccounts();
  }, [shippingId, shippingType, shippingService]);

  // Handle individual account selection
  const handleCheckboxChange = (accountId) => {
    setSelectedAccounts(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  // Handle "Select All" checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(accounts.map(a => a._id));
    }
    setSelectAll(!selectAll);
  };

  // Update "Select All" state when individual selections change
  useEffect(() => {
    if (accounts.length > 0) {
      setSelectAll(selectedAccounts.length === accounts.length);
    }
  }, [selectedAccounts, accounts]);

  // Save assigned accounts to backend
  const handleSave = async () => {
    if (selectedAccounts.length === 0) {
      Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one account',
        icon: 'warning',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    if (!shippingId) {
      Swal.fire({
        title: 'Error',
        text: 'Shipping type ID is missing. Please go back and try again.',
        icon: 'error',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    setSaving(true);

    try {
      const response = await fetch('https://hms.backend.hiragroupcloud.com/api/shipping/assign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shippingTypeId: shippingId,
          accountIds: selectedAccounts,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Success: Show alert for 2 seconds, then navigate
        Swal.fire({
          title: 'Success!',
          text: data.message || 'Shipping service assigned successfully!',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false
        });

        setTimeout(() => {
          navigate('/caseSetting');
        }, 2000);
      } else {
        // Backend error
        Swal.fire({
          title: 'Error',
          text: data.message || 'Failed to assign shipping service',
          icon: 'error',
          confirmButtonColor: '#004aad'
        });
      }
    } catch (error) {
      // Network or unexpected error
      Swal.fire({
        title: 'Network Error',
        text: 'Failed to save assignment. Please try again.',
        icon: 'error',
        confirmButtonColor: '#004aad'
      });
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Loading accounts...
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        {error}
        <button
          onClick={() => window.location.reload()}
          style={{ marginLeft: '10px', padding: '5px 10px' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Navvbar />
      <div style={{ width: '95%', margin: '0 auto' }}>
        <div className="row">
          {/* Left Sidebar */}
          <div className="col-md-2" style={{ borderRight: '1px solid #004aad' }}>
            <h2 style={{ marginLeft: '10px', color: '#004aad' }}>Settings</h2>
            {[
              { to: '/Tabs', label: 'Lab Details' },
              { to: '/', label: 'Store' },
              { to: '/usersetting', label: 'Users' },
              { to: '/', label: 'Permission Groups' },
              { to: '/Account', label: 'Accounts' },
              { to: '/Owerviews', label: 'Finances' },
              { to: '/owersetting', label: 'Case Settings', bold: true },
            ].map((it, i) => (
              <div key={i}>
                <Link
                  to={it.to}
                  style={{
                    textDecoration: 'none',
                    fontSize: '17px',
                    color: '#004aad',
                    marginLeft: it.label === 'Store' ? '13px' : '12px',
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: it.bold ? 'bold' : 'normal',
                  }}
                >
                  {it.label}
                </Link>
              </div>
            ))}
          </div>

          {/* Main Content */}
          <div className="col-md-10">
            <h1 style={{ marginBottom: '20px' }}>
              Assign "{shippingType || 'Local Driver'}"
            </h1>

            <h2 style={{ color: '#004aad', marginBottom: '10px' }}>Accounts</h2>

            <div
              style={{
                backgroundColor: '#f4f4f4',
                padding: '15px',
                borderTop: '2px solid #004aad',
              }}
            >
              {/* Select All */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  fontWeight: 'bold',
                }}
              >
                <label style={{ width: '220px' }}>Select All</label>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  style={{ transform: 'scale(1.3)', marginRight: '8px' }}
                />
              </div>

              {/* Accounts List */}
              <div>
                {accounts.length === 0 ? (
                  <p style={{ color: '#888', fontStyle: 'italic' }}>
                    No accounts found.
                  </p>
                ) : (
                  accounts.map(account => (
                    <div
                      key={account._id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '10px',
                        padding: '6px 0',
                      }}
                    >
                      <label
                        style={{
                          width: '220px',
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={account.account_name}
                      >
                        {account.account_name}
                      </label>

                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedAccounts.includes(account._id)}
                          onChange={() => handleCheckboxChange(account._id)}
                          style={{ transform: 'scale(1.2)', marginRight: '8px' }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>

              <p style={{ color: '#888', fontStyle: 'italic' }}>
                This will remove account level transit days overrides.
              </p>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  marginTop: '25px',
                  backgroundColor: saving ? '#666' : '#004aad',
                  color: '#fff',
                  padding: '12px 22px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                <PiPencilBold style={{ marginRight: '8px', fontSize: '18px' }} />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignAccounts;